/**
 * Post-incident review + stand-down — pure, I/O-free domain.
 *
 * The review is how the tool *learns* without ever asserting its own correctness:
 * the reviewer rates how useful each insight was, confirms whether the seeded
 * spatial facts (hydrant / access) matched reality, and can record new local
 * knowledge. None of this changes a past incident; it produces a structured
 * record (and audit events) that feed later doctrine/data tuning by an SME.
 *
 * Config-as-data: the stand-down checklist is a versioned list, SME-tunable.
 */

import type { Incident } from './incident.js';
import type { Insight } from './insights/index.js';

/** Stand-down checklist (stand-in items — review against brigade SOP). */
export interface ChecklistItem {
  id: string;
  label: string;
}

export const STAND_DOWN_CHECKLIST: readonly ChecklistItem[] = [
  { id: 'pars', label: 'Personnel accounted for (PAR complete)' },
  { id: 'ba-serviced', label: 'BA sets / cylinders accounted for and serviced' },
  { id: 'appliances-restored', label: 'Appliances restored to availability (water, fuel, equipment)' },
  { id: 'scene-handover', label: 'Scene handed to owner / police / investigator as required' },
  { id: 'hot-debrief', label: 'Hot debrief held with crews' },
  { id: 'injuries-logged', label: 'Injuries / near-misses logged' },
  { id: 'sitrep-final', label: 'Final SITREP / message passed to comms' },
];

/** How useful an insight was, judged after the fact. */
export type InsightRating = 'useful' | 'not-useful' | 'noise';

export const INSIGHT_RATING_LABELS: Record<InsightRating, string> = {
  useful: 'Useful',
  'not-useful': 'Not useful',
  noise: 'Noise / distracting',
};

export interface InsightFeedback {
  insightId: string;
  title: string;
  rating?: InsightRating;
  comment?: string;
}

/** Did a seeded spatial fact match what crews found? Unknown is first-class. */
export type AccuracyVerdict = 'accurate' | 'inaccurate' | 'not-checked';

export interface SpatialAccuracy {
  /** Nearest-hydrant accuracy as presented during the incident. */
  hydrant: AccuracyVerdict;
  /** Access / local-knowledge accuracy. */
  access: AccuracyVerdict;
  note?: string;
}

/** A new piece of local knowledge captured at review (feeds the seed later). */
export interface LocalNoteDraft {
  id: string;
  category: 'access' | 'water' | 'hazard' | 'exposure' | 'other';
  text: string;
}

export interface PostIncidentReview {
  incidentId: string;
  /** Checklist item id → checked. */
  checklist: Record<string, boolean>;
  spatial: SpatialAccuracy;
  insightFeedback: InsightFeedback[];
  localNotes: LocalNoteDraft[];
  /** Free-text overall learning point (optional). */
  summary?: string;
  completedAt?: string;
}

/** Build an empty review seeded with one feedback row per insight shown. */
export function createReview(incident: Incident, insights: readonly Insight[]): PostIncidentReview {
  // De-dupe by insight id; keep the most recent title.
  const byId = new Map<string, string>();
  for (const i of insights) byId.set(i.id, i.title);
  return {
    incidentId: incident.id,
    checklist: Object.fromEntries(STAND_DOWN_CHECKLIST.map((c) => [c.id, false])),
    spatial: { hydrant: 'not-checked', access: 'not-checked' },
    insightFeedback: [...byId.entries()].map(([insightId, title]) => ({ insightId, title })),
    localNotes: [],
  };
}

export interface ReviewProgress {
  checklistDone: number;
  checklistTotal: number;
  insightsRated: number;
  insightsTotal: number;
  spatialChecked: number;
  /** True when the checklist is complete and every insight has a rating. */
  ready: boolean;
}

export function reviewProgress(review: PostIncidentReview): ReviewProgress {
  const checklistDone = STAND_DOWN_CHECKLIST.filter((c) => review.checklist[c.id]).length;
  const insightsRated = review.insightFeedback.filter((f) => f.rating).length;
  const spatialChecked =
    (review.spatial.hydrant !== 'not-checked' ? 1 : 0) + (review.spatial.access !== 'not-checked' ? 1 : 0);
  const insightsTotal = review.insightFeedback.length;
  return {
    checklistDone,
    checklistTotal: STAND_DOWN_CHECKLIST.length,
    insightsRated,
    insightsTotal,
    spatialChecked,
    ready: checklistDone === STAND_DOWN_CHECKLIST.length && insightsRated === insightsTotal,
  };
}

/** Tally of insight usefulness — the headline learning metric. */
export interface UsefulnessTally {
  useful: number;
  'not-useful': number;
  noise: number;
  unrated: number;
}

export function tallyUsefulness(review: PostIncidentReview): UsefulnessTally {
  const t: UsefulnessTally = { useful: 0, 'not-useful': 0, noise: 0, unrated: 0 };
  for (const f of review.insightFeedback) {
    if (f.rating) t[f.rating] += 1;
    else t.unrated += 1;
  }
  return t;
}
